import type { FeedingDashboardConfig } from '@/domain/feedingDashboard';

/**
 * The one setting a pen cannot override: the feed/weight graph has a single x-axis,
 * so a per-pen bucket size could not be honoured. Hidden rather than disabled in pen
 * scope, since a greyed field invites the question every time.
 */
export const SITE_ONLY_FIELDS: ReadonlySet<string> = new Set(['weeklyResolutionDays']);

/**
 * Pellet fallthrough settings. Hidden, not disabled, while pellet is: an editable
 * threshold for a metric nobody can see reads as a feature that is live (IPS req 26).
 */
export const PELLET_FIELDS: ReadonlySet<string> = new Set([
    'fallthroughDepthThresholdM',
    'fallthroughWarnPct',
    'fallthroughAlertPct',
]);

/**
 * Every setting is numeric, so the generic number-input machinery below covers all
 * of them. Kept as a derived type rather than `keyof` so a future non-numeric
 * setting cannot silently join the numeric maps.
 */
export type FieldKey = {
    [K in keyof FeedingDashboardConfig]: FeedingDashboardConfig[K] extends number ? K : never;
}[keyof FeedingDashboardConfig];

export interface FieldDef {
    key: FieldKey;
    labelKey: string;
    step: number;
    /** true → must be > 0 (targets/depth); false → may be 0 (percent bands). */
    positive: boolean;
}

export interface SectionDef {
    id: string;
    titleKey: string;
    hintKey: string;
    fields: FieldDef[];
    /** [lowerKey, higherKey]: the lower bound must be ≤ the higher bound. */
    order?: [FieldKey, FieldKey];
}

export const C = 'feedingModule.config';

/** Reference targets. They double as chart reference lines; the depth one is pellet-only. */
export const TARGET_FIELDS: FieldDef[] = [
    { key: 'fcrTarget', labelKey: `${C}.fcrTarget`, step: 0.01, positive: true },
    { key: 'rgiTarget', labelKey: `${C}.fields.rgiTarget`, step: 1, positive: true },
    {
        key: 'fallthroughDepthThresholdM',
        labelKey: `${C}.depthThreshold`,
        step: 0.5,
        positive: true,
    },
];

/** Colour bands — one collapsible section per KPI. */
export const SECTIONS: SectionDef[] = [
    {
        id: 'fallthrough',
        titleKey: `${C}.sections.fallthrough`,
        hintKey: `${C}.hints.fallthrough`,
        order: ['fallthroughWarnPct', 'fallthroughAlertPct'],
        fields: [
            {
                key: 'fallthroughWarnPct',
                labelKey: `${C}.fields.elevatedFrom`,
                step: 0.5,
                positive: false,
            },
            {
                key: 'fallthroughAlertPct',
                labelKey: `${C}.fields.highAbove`,
                step: 0.5,
                positive: false,
            },
        ],
    },
    {
        id: 'fcr',
        titleKey: `${C}.sections.fcr`,
        hintKey: `${C}.hints.fcr`,
        fields: [
            { key: 'fcrWarnPct', labelKey: `${C}.fields.cautionAbove`, step: 0.5, positive: false },
        ],
    },
    {
        id: 'rgi',
        titleKey: `${C}.sections.rgi`,
        hintKey: `${C}.hints.rgi`,
        fields: [
            { key: 'rgiWarnPct', labelKey: `${C}.fields.cautionBelow`, step: 1, positive: false },
        ],
    },
    // No weight-deviation or feed-vs-plan band sections: the Pen Comparison spec has
    // no Bioscope-vs-PMS deviation column, and it colours feed vs plan by sign
    // (fed more = green, fed less = red) rather than by magnitude. Those config
    // fields remain in the API for compatibility but nothing reads them.
    {
        id: 'dataQuality',
        titleKey: `${C}.sections.dataQuality`,
        hintKey: `${C}.hints.dataQuality`,
        fields: [
            {
                key: 'bioscopeReadyDays',
                labelKey: `${C}.fields.bioscopeReadyDays`,
                step: 1,
                positive: true,
            },
            {
                key: 'weeklyResolutionDays',
                labelKey: `${C}.fields.weeklyResolutionDays`,
                step: 1,
                positive: true,
            },
        ],
    },
    {
        id: 'sfr',
        titleKey: `${C}.sections.sfr`,
        hintKey: `${C}.hints.sfr`,
        fields: [
            {
                key: 'sfrHealthyMinPct',
                labelKey: `${C}.fields.sfrHealthyMin`,
                step: 0.1,
                positive: false,
            },
            {
                key: 'sfrHealthyMaxPct',
                labelKey: `${C}.fields.sfrHealthyMax`,
                step: 0.1,
                positive: false,
            },
            {
                key: 'sfrOverfeedPct',
                labelKey: `${C}.fields.sfrOverfeed`,
                step: 0.1,
                positive: false,
            },
        ],
    },
    {
        id: 'confInterval',
        titleKey: `${C}.sections.confInterval`,
        hintKey: `${C}.hints.confInterval`,
        order: ['confIntervalGoodPct', 'confIntervalWarnPct'],
        fields: [
            {
                key: 'confIntervalGoodPct',
                labelKey: `${C}.fields.greenUpTo`,
                step: 0.5,
                positive: false,
            },
            {
                key: 'confIntervalWarnPct',
                labelKey: `${C}.fields.yellowUpTo`,
                step: 0.5,
                positive: false,
            },
        ],
    },
];

export const ALL_FIELDS: FieldDef[] = [
    ...TARGET_FIELDS,
    ...SECTIONS.flatMap((section) => section.fields),
];

export type ValueMap = Record<FieldKey, string>;
export type FlagMap = Partial<Record<FieldKey, boolean>>;

export function toStrings(config: FeedingDashboardConfig): ValueMap {
    const out = {} as ValueMap;
    for (const field of ALL_FIELDS) out[field.key] = String(config[field.key]);
    return out;
}
